import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Decimal, formaterIdentite, formaterPoints } from '@gsm/partage';
import { messageErreur, useRessource } from '../api/ressource';
import { Bloc, Tableau, type Colonne } from '../composants/Tableau';
import { BandeauAlerte } from '../composants/BandeauAlerte';
import { Nombre } from '../composants/Nombre';
import { Pastille } from '../composants/Pastille';
import { LienFiche } from '../composants/LienAction';
import { AInvestir } from './AInvestir';
import {
  BarreFiltres,
  FiltreChoix,
  FiltreRecherche,
  correspond,
  optionsDistinctes,
} from '../composants/Filtres';
import { Note } from '../composants/Note';

interface Ligne {
  mandatId: string;
  reference: string;
  client: { nom: string; prenom: string };
  profil: string;
  tresorerie: string;
  actifNet: string;
  tamponPct: string;
  tamponCible: string;
  ecartMontant: string;
  ecartPts: string | null;
  partPct: string | null;
}

interface Synthese {
  lignes: Ligne[];
  totaux: {
    nbMandats: number;
    tresorerie: string;
    actifNet: string;
    partPct: string | null;
    oisifs: number;
    sousTampon: number;
  };
  seuilOisivePts: string;
}

type Etat = 'SOUS_TAMPON' | 'OISIVE' | 'CONFORME';

const LIBELLE_ETAT: Record<Etat, string> = {
  SOUS_TAMPON: 'Sous le tampon',
  OISIVE: 'Trésorerie oisive',
  CONFORME: 'Conforme',
};

/** État d'un mandat au regard de son tampon ; le seuil est en points d'actif net. */
export function etatTresorerie(
  l: Pick<Ligne, 'ecartMontant' | 'ecartPts'>,
  seuilPts: string,
): Etat {
  if (new Decimal(l.ecartMontant).lt(0)) return 'SOUS_TAMPON';
  if (l.ecartPts !== null && new Decimal(l.ecartPts).gt(seuilPts)) return 'OISIVE';
  return 'CONFORME';
}

/**
 * « À investir » n'est pas un autre menu : c'est la même trésorerie, vue par
 * la file que le serveur décide. Le paramètre choisit la vue ; chaque vue tient
 * ses propres crochets, que la bascule ne mélange pas.
 */
export function Tresorerie() {
  const [requete] = useSearchParams();
  return requete.get('oisifs') === '1' ? <AInvestir /> : <SyntheseTresorerie />;
}

function SyntheseTresorerie() {
  const { donnees, chargement, erreur } = useRessource<Synthese>('/portefeuilles/tresorerie');
  const totaux = donnees?.totaux;
  const seuil = donnees?.seuilOisivePts ?? '0';
  const toutes = donnees?.lignes ?? [];

  const [recherche, setRecherche] = useState('');
  const [etatFiltre, setEtatFiltre] = useState('');
  const [profil, setProfil] = useState('');
  const filtresActifs = recherche !== '' || etatFiltre !== '' || profil !== '';
  const lignes = toutes.filter(
    (l) =>
      correspond(recherche, l.reference, l.client.nom, l.client.prenom) &&
      (!etatFiltre || etatTresorerie(l, seuil) === etatFiltre) &&
      (!profil || l.profil === profil),
  );

  /**
   * L'état se lit en toutes lettres, jamais à la couleur : le vert et le
   * rouge sont réservés à la performance (charte, § 2).
   */
  function etat(l: Ligne) {
    const e = etatTresorerie(l, seuil);
    return <Pastille ton={e === 'CONFORME' ? 'neutre' : 'attente'}>{LIBELLE_ETAT[e]}</Pastille>;
  }

  const colonnes: Colonne<Ligne>[] = [
    {
      cle: 'mandat',
      entete: 'Mandat',
      num: true,
      rendu: (l) => <LienFiche to={`/mandats/${l.mandatId}/tresorerie`}>{l.reference}</LienFiche>,
    },
    {
      cle: 'client',
      entete: 'Client',
      rendu: (l) => formaterIdentite(l.client.nom, l.client.prenom),
    },
    { cle: 'profil', entete: 'Profil', rendu: (l) => l.profil },
    {
      cle: 'tresorerie',
      entete: 'Trésorerie',
      num: true,
      rendu: (l) => <Nombre valeur={l.tresorerie} />,
    },
    {
      cle: 'part',
      entete: 'Part',
      num: true,
      rendu: (l) => (l.partPct === null ? '—' : <Nombre valeur={l.partPct} nature="pourcentage" />),
    },
    {
      cle: 'tampon',
      entete: 'Tampon',
      num: true,
      rendu: (l) => <Nombre valeur={l.tamponPct} nature="pourcentage" />,
    },
    {
      cle: 'cible',
      entete: 'Cible',
      num: true,
      rendu: (l) => <Nombre valeur={l.tamponCible} />,
    },
    {
      cle: 'ecart',
      entete: 'Écart',
      num: true,
      rendu: (l) => <Nombre valeur={l.ecartMontant} />,
    },
    {
      cle: 'ecartPts',
      entete: 'Écart',
      num: true,
      rendu: (l) => (l.ecartPts === null ? '—' : <Nombre valeur={l.ecartPts} nature="points" />),
    },
    // Pas de « Proposer des ordres » ici : l'investissement part de la file
    // « à investir », qui seule écarte les mandats déjà engagés.
    { cle: 'etat', entete: 'État', rendu: etat },
  ];

  return (
    <>
      <p className="entete__fil">Gestion</p>
      <div className="entete">
        <h1>Trésorerie</h1>
        <span className="entete__ref">{toutes.length} mandats en gestion</span>
      </div>

      {erreur && (
        <BandeauAlerte nature="refus" titre="Chargement impossible">
          {messageErreur(erreur)}
        </BandeauAlerte>
      )}

      {totaux && (
        <section className="cles" aria-label="Chiffres clés">
          <div className="cle">
            <span className="surtitre">Trésorerie sous gestion</span>
            <div className="cle__valeur">
              <Nombre valeur={totaux.tresorerie} />
              <small>MAD</small>
            </div>
            <div className="appoint">
              {totaux.partPct === null ? (
                '—'
              ) : (
                <>
                  <Nombre valeur={totaux.partPct} nature="pourcentage" /> de l’actif net
                </>
              )}
            </div>
          </div>
          <div className="cle">
            <span className="surtitre">Actif net</span>
            <div className="cle__valeur">
              <Nombre valeur={totaux.actifNet} />
              <small>MAD</small>
            </div>
          </div>
          <div className="cle">
            <span className="surtitre">Trésorerie oisive</span>
            <div className="cle__valeur">{totaux.oisifs}</div>
            <div className="appoint">
              {/* Un seuil n'est pas une variation : pas de signe explicite. */}
              mandats à plus de {formaterPoints(seuil).replace(/^\+/, '')} au-dessus du tampon
            </div>
          </div>
          <div className="cle">
            <span className="surtitre">Sous le tampon</span>
            <div className="cle__valeur">{totaux.sousTampon}</div>
            <div className="appoint">sous la réserve prévue par le profil</div>
          </div>
        </section>
      )}

      <Bloc
        titre="Par mandat"
        meta={
          chargement
            ? 'chargement…'
            : filtresActifs
              ? `${lignes.length} sur ${toutes.length}`
              : 'de l’excédent au manque'
        }
      >
        <BarreFiltres
          actifs={filtresActifs}
          onRetirer={() => {
            setRecherche('');
            setEtatFiltre('');
            setProfil('');
          }}
        >
          <FiltreRecherche
            valeur={recherche}
            onChange={setRecherche}
            placeholder="Référence, nom ou prénom du client"
          />
          <FiltreChoix
            libelle="État"
            tous="Tous les états"
            valeur={etatFiltre}
            onChange={setEtatFiltre}
            options={Object.entries(LIBELLE_ETAT)}
          />
          <FiltreChoix
            libelle="Profil"
            tous="Tous les profils"
            valeur={profil}
            onChange={setProfil}
            options={optionsDistinctes(toutes, (l) => [l.profil, l.profil])}
          />
        </BarreFiltres>
        <Tableau
          colonnes={colonnes}
          lignes={lignes}
          cle={(l) => l.mandatId}
          vide={
            filtresActifs ? 'Aucun mandat ne répond à ces critères.' : 'Aucun mandat en gestion.'
          }
          pied={
            // Les totaux portent sur tous les mandats : sous filtre, ils
            // contrediraient les lignes affichées.
            totaux && lignes.length > 0 && !filtresActifs ? (
              <tfoot>
                <tr>
                  <td colSpan={3}>
                    <b>Total — {totaux.nbMandats} mandat(s)</b>
                  </td>
                  <td className="num">
                    <Nombre valeur={totaux.tresorerie} />
                  </td>
                  <td className="num">
                    {totaux.partPct === null ? (
                      '—'
                    ) : (
                      <Nombre valeur={totaux.partPct} nature="pourcentage" />
                    )}
                  </td>
                  <td colSpan={5} />
                </tr>
              </tfoot>
            ) : undefined
          }
        />
        <Note resume="L’écart se mesure au tampon du profil.">
          Le tampon est une réserve d’espèces en pourcentage de l’actif net. Les soldes sont ceux de
          la dernière situation reçue du back-office.
        </Note>
      </Bloc>
    </>
  );
}
